import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, ChevronDown, Trash2, Plus, Upload, FileText, Loader2, AlertCircle, Check } from 'lucide-react';
import { supabase } from './supabaseClient';
import { uploadFileToSupabase } from './supabaseStorage';

const BRAND = '#163248';
const UNIT_OPTIONS = ['шт.', 'міш.', 'каніс.', 'рул.', 'карта', 'уп.', 'л', 'кг', 'т', 'м', 'м²', 'м.п.'];

const STATUS_CONFIG = {
  collecting: { label: 'Збирається', bg: '#fef3c7', color: '#d97706' },
  quote_received: { label: 'КП отримана', bg: '#dbeafe', color: '#2563eb' },
  added_to_estimate: { label: 'В кошторисі', bg: '#dcfce7', color: '#16a34a' },
};

// Відмінювання одиниць
function pluralize(n, unit) {
  const num = Math.abs(parseFloat(n)) || 0;
  const last1 = num % 10;
  const last2 = num % 100;
  const forms = {
    'міш.': ['мішок', 'мішки', 'мішків'],
    'мішок': ['мішок', 'мішки', 'мішків'],
    'каніс.': ['каністра', 'каністри', 'каністр'],
    'каністра': ['каністра', 'каністри', 'каністр'],
    'рул.': ['рулон', 'рулони', 'рулонів'],
    'рулон': ['рулон', 'рулони', 'рулонів'],
    'карта': ['карта', 'карти', 'карт'],
    'уп.': ['упаковка', 'упаковки', 'упаковок'],
    'упаковка': ['упаковка', 'упаковки', 'упаковок'],
  };
  if (!forms[unit]) return unit;
  const [one, few, many] = forms[unit];
  if (last2 >= 11 && last2 <= 19) return many;
  if (last1 === 1) return one;
  if (last1 >= 2 && last1 <= 4) return few;
  return many;
}

// Кастомний dropdown для одиниць виміру (через портал)
function UnitSelect({ value, onChange, displayLabel }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 120 });
  const btnRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target) && !e.target.closest('.unit-select-portal')) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const handleOpen = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const dropLeft = Math.max(8, Math.min(r.right - 120, window.innerWidth - 128));
      setPos({ top: r.bottom + 4, left: dropLeft, width: 120 });
    }
    setOpen(!open);
  };

  return (
    <div style={{ position: 'relative', width: 80 }}>
      <button ref={btnRef} onClick={handleOpen}
        style={{
          width: '100%', padding: '6px 20px 6px 8px', border: `1.5px solid ${open ? BRAND : '#e5e7eb'}`,
          borderRadius: 8, fontSize: 12, color: '#6b7280', background: '#fafaf9',
          fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left', position: 'relative',
          whiteSpace: 'nowrap', transition: 'border-color .15s', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {displayLabel || value}
        <ChevronDown style={{ position: 'absolute', right: 4, top: '50%', transform: `translateY(-50%) ${open ? 'rotate(180deg)' : 'rotate(0deg)'}`, width: 12, height: 12, color: '#9ca3af', transition: 'transform .15s' }} />
      </button>
      {open && ReactDOM.createPortal(
        <div className="unit-select-portal" style={{
          position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
          background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 200, overflow: 'hidden',
          maxHeight: 200, overflowY: 'auto',
        }}>
          {UNIT_OPTIONS.map((u, i) => (
            <button key={u} onClick={() => { onChange(u); setOpen(false); }}
              style={{
                width: '100%', padding: '8px 12px', border: 'none',
                background: u === value ? '#f0f4f8' : '#fff',
                cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
                color: u === value ? BRAND : '#374151',
                fontWeight: u === value ? 600 : 400,
                textAlign: 'left', display: 'block',
                borderBottom: i < UNIT_OPTIONS.length - 1 ? '1px solid #f3f4f6' : 'none',
                transition: 'background .1s',
              }}
              onMouseEnter={e => { if (u !== value) e.target.style.background = '#fafaf9'; }}
              onMouseLeave={e => { if (u !== value) e.target.style.background = '#fff'; }}
            >{u}</button>
          ))}
        </div>,
      document.body)}
    </div>
  );
}

// Кастомний dropdown для об'єкта
function ProjectSelect({ value, projectName, projects, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  const btnRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="relative">
      <button ref={btnRef} type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-sm px-3 py-2 border border-gray-200 rounded-xl bg-white hover:border-gray-300 transition-colors"
        style={{ color: projectName ? BRAND : '#9ca3af' }}
      >
        <span className="truncate">{projectName || '— Не прив\'язано —'}</span>
        <ChevronDown className="w-4 h-4 flex-shrink-0 text-gray-400" />
      </button>
      {open && (
        <div ref={ref} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
          <button type="button" onClick={() => { onChange(null, null); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 italic">
            — Не прив'язано —
          </button>
          {(projects || []).map(p => (
            <button key={p.id} type="button" onClick={() => { onChange(String(p.id), p.name); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
              style={{ color: BRAND }}>
              {p.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== ОСНОВНИЙ КОМПОНЕНТ ====================

export default function OrderViewModal({ order, projects, onClose, onSave, onDelete, user }) {
  const [editingOrder, setEditingOrder] = useState(order);
  const [newMaterial, setNewMaterial] = useState({ name: '', qty: '', unit: 'шт.' });
  const [uploadingQuote, setUploadingQuote] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null); // результат розпізнання від AI
  const [quoteError, setQuoteError] = useState('');
  const [renameDecisions, setRenameDecisions] = useState({}); // { orderIndex: true/false } — замінити назву
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const fileInputRef = useRef(null);

  if (!editingOrder) return null;

  const status = editingOrder.status || 'collecting';
  const isCollecting = status === 'collecting';
  const isQuoteReceived = status === 'quote_received';
  const isAddedToEstimate = status === 'added_to_estimate';
  const statusCfg = STATUS_CONFIG[status] || { label: status, bg: '#f3f4f6', color: '#6b7280' };

  // Підсумки
  const totalSupplier = (editingOrder.materials || []).reduce((sum, m) => sum + ((m.workPrice || 0) * (m.qty || 0)), 0);
  const totalClient = (editingOrder.materials || []).reduce((sum, m) => sum + ((m.clientPrice || 0) * (m.qty || 0)), 0);

  // ── Завантаження файлу КП + розпізнання через AI ──
  // ── Дедуплікація: знаходить дублікат в масиві materials ──
  const normalizeName = (name) => (name || '').toLowerCase().replace(/\s+/g, ' ').replace(/[^а-яіїєґa-z0-9\s.,×/()-]/gi, '').trim();

  const findDuplicate = (materials, newItem) => {
    // 1. По productId (точний)
    if (newItem.productId) {
      const idx = materials.findIndex(m => m.productId === newItem.productId);
      if (idx >= 0) return idx;
    }
    // 2. По нормалізованій назві
    const normalNew = normalizeName(newItem.name);
    if (!normalNew) return -1;
    return materials.findIndex(m => normalizeName(m.name) === normalNew);
  };

  // Додає матеріал з дедуплікацією. Повертає { materials, merged, mergedName }
  const addMaterialWithDedup = (materials, newItem) => {
    const dupIdx = findDuplicate(materials, newItem);
    if (dupIdx >= 0) {
      const existing = materials[dupIdx];
      const updated = [...materials];
      // Перевірка сумісності для factQty мерджу
      const canFactMerge = existing.factQty && newItem.factQty && existing.factUnit === newItem.factUnit && existing.packSize === newItem.packSize;
      let newQty, newFactQty;
      if (canFactMerge) {
        newFactQty = existing.factQty + newItem.factQty;
        newQty = Math.ceil(newFactQty / (existing.packSize || 1));
      } else {
        newQty = (existing.qty || 0) + (newItem.qty || 0);
        newFactQty = existing.factQty || null;
      }
      updated[dupIdx] = {
        ...existing,
        qty: newQty,
        factQty: newFactQty,
        workPrice: existing.workPrice || newItem.workPrice || 0,
        clientPrice: existing.clientPrice || newItem.clientPrice || 0,
      };
      return { materials: updated, merged: true, mergedName: existing.name, oldQty: existing.qty, newQty };
    }
    return { materials: [...materials, { id: Date.now() + Math.random(), ...newItem }], merged: false };
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      // Відрізаємо "data:application/pdf;base64," префікс
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleUploadQuote = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Валідація типу
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setQuoteError('Підтримуються формати PDF, JPG, PNG');
      return;
    }

    // Валідація розміру (10 MB)
    if (file.size > 10 * 1024 * 1024) {
      setQuoteError('Файл завеликий (макс 10 MB)');
      return;
    }

    setQuoteError('');
    setUploadingQuote(true);

    try {
      // 1. Завантажуємо файл в Supabase Storage
      const uploaded = await uploadFileToSupabase(file, user.id, `supplier-quotes_${editingOrder.id || 'new'}`);

      // 2. Зберігаємо URL і name в замовленні (одразу в БД якщо id є)
      const fileUpdates = {
        supplier_file_url: uploaded.url,
        supplier_file_name: uploaded.name,
      };
      setEditingOrder(prev => ({ ...prev, ...fileUpdates }));
      if (editingOrder.id) {
        await supabase.from('order_cards').update(fileUpdates).eq('id', editingOrder.id);
      }

      // 3. Готуємо base64 і викликаємо Edge Function
      const base64 = await fileToBase64(file);
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const resp = await fetch('https://jkouufahpliqgyvcmbwb.supabase.co/functions/v1/recognize-supplier-quote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || ''}`,
        },
        body: JSON.stringify({
          fileBase64: base64,
          mimeType: file.type,
          orderMaterials: (editingOrder.materials || []).map(m => ({ name: m.name, qty: m.qty, unit: m.unit })),
        }),
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${resp.status}`);
      }

      const data = await resp.json();
      if (!data.result) throw new Error('Порожня відповідь від AI');

      setQuoteResult(data.result);
      // Ініціалізуємо renameDecisions: для всіх matches де назва змінилась — ставимо true (замінити)
      const decisions = {};
      (data.result?.matches || []).forEach(m => {
        const orderMat = (editingOrder.materials || [])[m.orderIndex];
        if (orderMat && m.matchedName && m.matchedName.trim() !== orderMat.name.trim()) {
          decisions[m.orderIndex] = true;
        }
      });
      setRenameDecisions(decisions);
      console.log('[OrderViewModal] Recognize result:', data.result);
    } catch (err) {
      console.error('[OrderViewModal] handleUploadQuote error:', err);
      setQuoteError(err.message || 'Помилка розпізнання');
    } finally {
      setUploadingQuote(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Застосувати результат розпізнання до матеріалів замовлення ──
  const applyQuoteResult = async () => {
    if (!quoteResult) return;

    // 1. Копія матеріалів
    const updatedMaterials = [...(editingOrder.materials || [])];

    // 2. Для кожного match — оновлюємо workPrice, name (якщо юзер підтвердив заміну), originalName
    (quoteResult.matches || []).forEach(match => {
      const idx = match.orderIndex;
      if (idx < 0 || idx >= updatedMaterials.length) return;
      const m = updatedMaterials[idx];
      const originalName = m.name;
      const shouldRename = renameDecisions[idx] === true && match.matchedName;
      updatedMaterials[idx] = {
        ...m,
        workPrice: match.unitPrice || 0,
        clientPrice: match.unitPrice || 0, // за замовчуванням = workPrice, юзер правитиме
        ...(shouldRename ? { name: match.matchedName, originalName } : {}),
      };
    });

    // 3. Додаємо unmatched з дедуплікацією (одиниця завжди шт.)
    (quoteResult.unmatched || []).forEach(u => {
      const newItem = { name: u.name, qty: u.qty || 1, unit: 'шт.', workPrice: u.unitPrice || 0, clientPrice: u.unitPrice || 0 };
      const result = addMaterialWithDedup(updatedMaterials, newItem);
      updatedMaterials.splice(0, updatedMaterials.length, ...result.materials);
    });

    // 4. Зберігаємо + статус quote_received
    try {
      const updates = {
        materials: updatedMaterials,
        status: 'quote_received',
      };
      if (editingOrder.id) {
        await supabase.from('order_cards').update(updates).eq('id', editingOrder.id);
      }
      setEditingOrder(prev => ({ ...prev, ...updates }));
      setQuoteResult(null);
      setRenameDecisions({});
      if (window.showToast) window.showToast('КП застосовано, статус оновлено');
    } catch (err) {
      console.error('[OrderViewModal] applyQuoteResult error:', err);
      if (window.showToast) window.showToast('Помилка збереження');
    }
  };

  const handleSave = async () => {
    try {
      // Якщо є незбережений матеріал у полі "Додати" — додаємо його до списку
      let materialsList = [...(editingOrder.materials || [])];
      if (newMaterial.name.trim()) {
        const newItem = { name: newMaterial.name.trim(), qty: parseFloat(newMaterial.qty) || 0, unit: newMaterial.unit };
        const result = addMaterialWithDedup(materialsList, newItem);
        materialsList = result.materials;
      }
      // Нормалізуємо materials: qty має бути number, не ''
      const normalizedMaterials = materialsList.map(m => ({
        ...m,
        qty: m.qty === '' || m.qty === null || m.qty === undefined ? 0 : (parseFloat(m.qty) || 0),
      }));
      console.log('[OrderViewModal] handleSave — editingOrder:', editingOrder);
      console.log('[OrderViewModal] handleSave — normalized materials:', normalizedMaterials);

      if (editingOrder.id) {
        const updates = {
          name: editingOrder.name,
          materials: normalizedMaterials,
          delivery: editingOrder.delivery || null,
          project_id: editingOrder.project_id || null,
          project_name: editingOrder.project_name || null,
          status: editingOrder.status || 'collecting',
          supplier_file_url: editingOrder.supplier_file_url || null,
          supplier_file_name: editingOrder.supplier_file_name || null,
        };
        const { error } = await supabase.from('order_cards').update(updates).eq('id', editingOrder.id);
        if (error) { console.error('[OrderViewModal] UPDATE error:', error); throw error; }
        onSave({ ...editingOrder, ...updates });
      } else {
        if (!user?.id) return;
        const { data, error } = await supabase.from('order_cards').insert({
          user_id: user.id,
          name: editingOrder.name || 'Нове замовлення',
          materials: normalizedMaterials,
          delivery: editingOrder.delivery || null,
          project_id: editingOrder.project_id || null,
          project_name: editingOrder.project_name || null,
          status: 'collecting',
        }).select().single();
        if (error) { console.error('[OrderViewModal] INSERT error:', error); throw error; }
        onSave(data, true);
      }
      if (window.showToast) window.showToast('Збережено!');
    } catch (e) {
      console.error('[OrderViewModal] handleSave error:', e);
      if (window.showToast) window.showToast('Помилка збереження');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 px-0 sm:px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl sm:max-w-2xl w-full max-h-[85vh] shadow-2xl flex flex-col" style={{ overflow: 'hidden' }}>

        {/* Header */}
        <div className="px-5 py-4 border-b flex-shrink-0" style={{ borderColor: '#e5e7eb' }}>
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 mr-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="text-[10px] uppercase tracking-wider text-gray-400">Замовлення</div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setStatusDropdownOpen(o => !o)}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                    style={{ background: statusCfg.bg, color: statusCfg.color }}
                  >
                    {statusCfg.label}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {statusDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setStatusDropdownOpen(false)} />
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 min-w-[160px] overflow-hidden">
                        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={async () => {
                              setStatusDropdownOpen(false);
                              if (key === status) return;
                              setEditingOrder(prev => ({ ...prev, status: key }));
                              if (editingOrder.id) {
                                try {
                                  await supabase.from('order_cards').update({ status: key }).eq('id', editingOrder.id);
                                  if (window.showToast) window.showToast(`Статус: ${cfg.label}`);
                                } catch (e) { console.error('Status update error:', e); }
                              }
                            }}
                            className={`w-full text-left px-3 py-2 text-xs font-medium hover:bg-gray-50 transition-colors flex items-center gap-2 ${key === status ? 'bg-gray-50' : ''}`}
                          >
                            <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ background: cfg.color }} />
                            <span style={{ color: cfg.color }}>{cfg.label}</span>
                            {key === status && <Check className="w-3 h-3 ml-auto" style={{ color: cfg.color }} />}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <input type="text" value={editingOrder.name}
                onChange={(e) => setEditingOrder(prev => ({ ...prev, name: e.target.value }))}
                readOnly={!isCollecting}
                className="text-base font-bold bg-transparent focus:outline-none focus:bg-gray-50 focus:px-2 rounded w-full"
                style={{ color: BRAND, border: 'none', padding: '2px 0', fontFamily: 'inherit' }}
                placeholder="Назва замовлення"
              />
            </div>
            <button onClick={onClose} style={{
              width: 32, height: 32, borderRadius: '50%', border: '1.5px solid #e5e7eb',
              background: 'transparent', cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0,
            }}>
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          {/* Прив'язка до об'єкта */}
          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 block">Об'єкт</label>
            {!isCollecting ? (
              <div className="text-sm px-3 py-2" style={{ color: BRAND }}>{editingOrder.project_name || 'Не прив\'язано'}</div>
            ) : (
              <ProjectSelect
                value={editingOrder.project_id}
                projectName={editingOrder.project_name}
                projects={projects}
                onChange={(pid, pname) => setEditingOrder(prev => ({ ...prev, project_id: pid, project_name: pname }))}
              />
            )}
          </div>
        </div>

        {/* Body — матеріали */}
        <div className="overflow-y-auto flex-1 px-4 py-3 modal-scroll" style={{ overscrollBehavior: 'contain' }}>
          {(editingOrder.materials || []).map((m, idx) => {
            const displayUnit = ['міш.', 'каніс.', 'рул.', 'карта', 'уп.'].includes(m.unit)
              ? pluralize(m.qty, m.unit) : m.unit;

            const materialClientPrice = m.clientPrice || 0;
            const materialSupplierPrice = m.workPrice || 0;
            const rowSum = materialClientPrice * (m.qty || 0);

            return (
              <div key={m.id || idx} className="py-3" style={{ borderBottom: idx < (editingOrder.materials || []).length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                <div className="flex items-start gap-3">
                  <span className="text-[11px] text-gray-400 mt-1 w-5 flex-shrink-0 text-right">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <input type="text" value={m.name}
                      onChange={(e) => {
                        const upd = { ...editingOrder };
                        upd.materials = [...upd.materials];
                        upd.materials[idx] = { ...upd.materials[idx], name: e.target.value };
                        setEditingOrder(upd);
                      }}
                      readOnly={!isCollecting}
                      className="text-[13px] font-medium bg-transparent focus:outline-none focus:bg-gray-50 focus:px-2 rounded w-full"
                      style={{ color: '#1f2937', lineHeight: '1.4', border: 'none', padding: '2px 0', fontFamily: 'inherit' }}
                    />
                    {m.originalName && (
                      <div className="text-[10px] text-amber-600 mt-0.5 italic">Замінено аналогом: було {m.originalName}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0" style={{ justifyContent: 'flex-end' }}>
                    {m.factQty && m.factUnit && (
                      <span className="text-[11px] text-gray-400 whitespace-nowrap">({m.factQty} {m.factUnit})</span>
                    )}
                    {isCollecting ? (
                      <input type="number" value={m.qty || ''}
                        onChange={(e) => {
                          const upd = { ...editingOrder };
                          upd.materials = [...upd.materials];
                          upd.materials[idx] = { ...upd.materials[idx], qty: e.target.value === '' ? '' : parseFloat(e.target.value) || 0 };
                          setEditingOrder(upd);
                        }}
                        className="text-center bg-white focus:outline-none"
                        style={{ width: 56, padding: '6px 4px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 600, color: BRAND, fontFamily: 'inherit' }}
                        inputMode="decimal"
                        onFocus={e => { e.target.style.borderColor = BRAND; }}
                        onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                      />
                    ) : (
                      <span className="text-[13px] font-semibold tabular-nums" style={{ width: 56, textAlign: 'center', color: BRAND }}>{m.qty || 0}</span>
                    )}
                    {isCollecting ? (
                      <UnitSelect
                        value={m.unit || 'шт.'}
                        displayLabel={displayUnit}
                        onChange={(u) => {
                          const upd = { ...editingOrder };
                          upd.materials = [...upd.materials];
                          upd.materials[idx] = { ...upd.materials[idx], unit: u };
                          setEditingOrder(upd);
                        }}
                      />
                    ) : (
                      <span className="text-xs text-gray-500" style={{ width: 80, textAlign: 'left', paddingLeft: 8 }}>{displayUnit}</span>
                    )}
                    {isCollecting && (
                      <button onClick={() => {
                        const upd = { ...editingOrder };
                        upd.materials = upd.materials.filter((_, i) => i !== idx);
                        setEditingOrder(upd);
                      }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, opacity: 0.4 }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; }}
                      >
                        <Trash2 style={{ width: 14, height: 14, color: '#ef4444' }} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Ціни (показуються тільки якщо є workPrice від постачальника або можна ввести clientPrice) */}
                {(materialSupplierPrice > 0 || isQuoteReceived) && (
                  <div className="flex items-center gap-2 mt-2 ml-8 text-[11px]">
                    {materialSupplierPrice > 0 && (
                      <div className="text-gray-500">
                        Постачальник: <span className="font-medium tabular-nums">{materialSupplierPrice.toLocaleString('uk-UA')} ₴</span>
                      </div>
                    )}
                    {(isQuoteReceived || materialClientPrice > 0) && (
                      <div className="flex items-center gap-1.5 ml-auto">
                        <span className="text-gray-500">Клієнту:</span>
                        {isQuoteReceived ? (
                          <input type="number" value={m.clientPrice || ''}
                            onChange={(e) => {
                              const upd = { ...editingOrder };
                              upd.materials = [...upd.materials];
                              upd.materials[idx] = { ...upd.materials[idx], clientPrice: parseFloat(e.target.value) || 0 };
                              setEditingOrder(upd);
                            }}
                            className="text-right bg-white focus:outline-none"
                            style={{ width: 70, padding: '3px 6px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 11, fontWeight: 600, color: BRAND, fontFamily: 'inherit' }}
                            inputMode="decimal"
                            onFocus={e => { e.target.style.borderColor = BRAND; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                          />
                        ) : (
                          <span className="font-semibold tabular-nums" style={{ color: BRAND }}>{materialClientPrice.toLocaleString('uk-UA')} ₴</span>
                        )}
                        {rowSum > 0 && <span className="text-gray-400">= {rowSum.toLocaleString('uk-UA')} ₴</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Додати матеріал */}
          {isCollecting && (
            <div className="flex items-center gap-2 pt-3 mt-1" style={{ borderTop: '1px solid #f3f4f6' }}>
              <input type="text" value={newMaterial.name}
                onChange={(e) => { let val = e.target.value; if (val.length > 0) val = val[0].toUpperCase() + val.slice(1); setNewMaterial(p => ({ ...p, name: val })); }}
                placeholder="Додати матеріал..."
                className="flex-1 text-[13px] bg-white focus:outline-none min-w-0"
                style={{ padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontFamily: 'inherit' }}
                onFocus={e => { e.target.style.borderColor = BRAND; }}
                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
              />
              <input type="number" value={newMaterial.qty}
                onChange={(e) => setNewMaterial(p => ({ ...p, qty: e.target.value }))}
                placeholder="0"
                className="text-center bg-white focus:outline-none flex-shrink-0"
                style={{ width: 56, padding: '8px 4px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, fontFamily: 'inherit' }}
                inputMode="decimal"
                onFocus={e => { e.target.style.borderColor = BRAND; }}
                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
              />
              <UnitSelect
                value={newMaterial.unit}
                onChange={(u) => setNewMaterial(p => ({ ...p, unit: u }))}
              />
              <button
                onClick={() => {
                  if (!newMaterial.name.trim()) return;
                  const newItem = { name: newMaterial.name.trim(), qty: parseFloat(newMaterial.qty) || 0, unit: newMaterial.unit };
                  const result = addMaterialWithDedup(editingOrder.materials || [], newItem);
                  setEditingOrder(prev => ({ ...prev, materials: result.materials }));
                  if (result.merged && window.showToast) window.showToast(`"${result.mergedName}" — кількість збільшено: ${result.oldQty} → ${result.newQty}`);
                  setNewMaterial({ name: '', qty: '', unit: 'шт.' });
                }}
                style={{
                  width: 36, height: 36, borderRadius: 10, border: 'none', cursor: 'pointer',
                  background: newMaterial.name.trim() ? BRAND : '#e5e7eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  transition: 'background .15s',
                }}
              >
                <Plus className="w-4 h-4 text-white" />
              </button>
            </div>
          )}

          {/* Підсумок цін */}
          {(totalSupplier > 0 || totalClient > 0) && (
            <div className="mt-4 pt-3 space-y-1" style={{ borderTop: '1px solid #f3f4f6' }}>
              {totalSupplier > 0 && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Від постачальника:</span>
                  <span className="font-semibold tabular-nums">{totalSupplier.toLocaleString('uk-UA')} ₴</span>
                </div>
              )}
              {totalClient > 0 && (
                <div className="flex justify-between text-sm font-semibold" style={{ color: BRAND }}>
                  <span>Для клієнта:</span>
                  <span className="tabular-nums">{totalClient.toLocaleString('uk-UA')} ₴</span>
                </div>
              )}
            </div>
          )}

          {/* Доставка */}
          <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f3f4f6' }}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!editingOrder.delivery?.needed}
                disabled={!isCollecting}
                onChange={(e) => setEditingOrder(prev => ({
                  ...prev,
                  delivery: e.target.checked
                    ? { needed: true, date: prev.delivery?.date || '', address: prev.delivery?.address || '' }
                    : null
                }))}
                className="w-4 h-4 rounded border-gray-300"
                style={{ accentColor: BRAND }}
              />
              <span className="text-[13px] font-medium" style={{ color: BRAND }}>Потрібна доставка</span>
            </label>
            {editingOrder.delivery?.needed && (
              <div className="flex gap-2 mt-2">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Дата доставки</div>
                  <input type="date" value={editingOrder.delivery?.date || ''}
                    onChange={(e) => setEditingOrder(prev => ({ ...prev, delivery: { ...prev.delivery, date: e.target.value } }))}
                    readOnly={!isCollecting}
                    className="w-full text-[13px] bg-white focus:outline-none"
                    style={{ padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontFamily: 'inherit' }}
                  />
                </div>
                <div className="flex-[2]">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Адреса</div>
                  <input type="text" value={editingOrder.delivery?.address || ''}
                    onChange={(e) => setEditingOrder(prev => ({ ...prev, delivery: { ...prev.delivery, address: e.target.value } }))}
                    readOnly={!isCollecting}
                    placeholder="Вулиця, будинок, квартира"
                    className="w-full text-[13px] bg-white focus:outline-none"
                    style={{ padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontFamily: 'inherit' }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* КП постачальника */}
          {!isAddedToEstimate && (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f3f4f6' }}>
              <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">КП постачальника</div>
              {editingOrder.supplier_file_url && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl mb-2" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                  <FileText className="w-4 h-4 flex-shrink-0" style={{ color: BRAND }} />
                  <a href={editingOrder.supplier_file_url} target="_blank" rel="noopener noreferrer" className="flex-1 text-xs truncate hover:underline" style={{ color: BRAND }}>
                    {editingOrder.supplier_file_name || 'Файл КП'}
                  </a>
                </div>
              )}
              <input type="file" ref={fileInputRef} onChange={handleUploadQuote} accept="application/pdf,image/jpeg,image/jpg,image/png" style={{ display: 'none' }} />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingQuote}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all"
                style={{ border: `1.5px dashed ${uploadingQuote ? '#9ca3af' : BRAND}`, color: uploadingQuote ? '#9ca3af' : BRAND, background: uploadingQuote ? '#f3f4f6' : 'white' }}
              >
                {uploadingQuote ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Розпізнаю КП...</>
                ) : (
                  <><Upload className="w-4 h-4" /> {editingOrder.supplier_file_url ? 'Замінити файл КП' : 'Додати файл КП'}</>
                )}
              </button>
              {uploadingQuote && (
                <div className="mt-2 text-center text-[11px] text-gray-400 italic">
                  {(() => {
                    const tips = [
                      'AI аналізує файл постачальника і шукає ціни...',
                      'Розпізнає назви, кількості та ціни з документа',
                      'Якщо постачальник запропонував аналог — AI це зазначить',
                      'Підтримуються PDF, JPG та PNG файли',
                      'Чим якісніший скан — тим точніше розпізнання',
                    ];
                    return tips[Math.floor(Math.random() * tips.length)];
                  })()}
                </div>
              )}
              {quoteError && (
                <div className="mt-2 px-3 py-2 rounded-lg text-[11px] flex items-start gap-1.5" style={{ background: '#fef2f2', color: '#991b1b' }}>
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{quoteError}</span>
                </div>
              )}
            </div>
          )}

          {/* Інформація про додано до кошторису */}
          {isAddedToEstimate && (editingOrder.estimate_id || editingOrder.section_id) && (
            <div className="mt-3 p-3 rounded-xl" style={{ background: '#dcfce7' }}>
              <div className="text-[11px] text-gray-600">Додано до кошторису</div>
              <div className="text-sm font-medium mt-0.5" style={{ color: '#166534' }}>
                Замовлення перетворене на позиції в кошторисі
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex gap-3" style={{ flexShrink: 0, borderColor: '#e5e7eb' }}>
          {isAddedToEstimate ? (
            <>
              <button onClick={onClose}
                className="flex-1 py-2.5 bg-white rounded-xl font-semibold text-[13px]"
                style={{ color: BRAND, border: '1.5px solid #163248' }}>
                Закрити
              </button>
              {onDelete && (
                <button onClick={() => onDelete(editingOrder.id)}
                  className="px-4 py-2.5 rounded-xl font-semibold text-[13px]"
                  style={{ color: '#ef4444', border: '1.5px solid #fecaca', background: '#fef2f2' }}>
                  Видалити
                </button>
              )}
            </>
          ) : (
            <>
              <button onClick={onClose}
                className="flex-1 py-2.5 bg-white rounded-xl font-semibold text-[13px]"
                style={{ color: BRAND, border: '1.5px solid #163248' }}>
                Скасувати
              </button>
              <button onClick={handleSave}
                className="flex-1 py-2.5 text-white rounded-xl font-semibold text-[13px]"
                style={{ backgroundColor: BRAND }}>
                Зберегти
              </button>
            </>
          )}
        </div>
      </div>

      {/* Модалка результату розпізнання КП */}
      {quoteResult && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-[60] px-0 sm:px-4" onClick={(e) => { if (e.target === e.currentTarget) setQuoteResult(null); }}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl sm:max-w-2xl w-full max-h-[90vh] shadow-2xl flex flex-col" style={{ overflow: 'hidden' }}>
            {/* Header */}
            <div className="px-5 py-4 border-b flex-shrink-0 flex items-center justify-between" style={{ borderColor: '#e5e7eb' }}>
              <div>
                <h3 className="text-base font-bold" style={{ color: BRAND }}>Результат розпізнання КП</h3>
                {quoteResult.supplierName && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {quoteResult.supplierName}
                    {quoteResult.quoteNumber && ` · ${quoteResult.quoteNumber}`}
                    {quoteResult.quoteDate && ` · ${quoteResult.quoteDate}`}
                  </div>
                )}
              </div>
              <button onClick={() => setQuoteResult(null)} style={{ width: 32, height: 32, borderRadius: '50%', border: '1.5px solid #e5e7eb', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 px-5 py-4">
              {quoteResult.error && (
                <div className="px-4 py-3 rounded-xl mb-4 flex items-start gap-2" style={{ background: '#fef2f2', color: '#991b1b' }}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{quoteResult.error}</span>
                </div>
              )}

              {/* Matches */}
              {quoteResult.matches && quoteResult.matches.length > 0 && (
                <div className="mb-4">
                  <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Зіставлені позиції ({quoteResult.matches.length})</div>
                  <div className="space-y-2">
                    {quoteResult.matches.map((match, idx) => {
                      const orderMat = (editingOrder.materials || [])[match.orderIndex];
                      const nameChanged = orderMat && match.matchedName && match.matchedName.trim() !== orderMat.name.trim();
                      const willRename = renameDecisions[match.orderIndex] === true;
                      return (
                        <div key={idx} className="px-3 py-2 rounded-lg" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Check className="w-4 h-4 flex-shrink-0" style={{ color: '#16a34a' }} />
                                <span className="text-[13px] font-medium line-clamp-2 break-words" style={{ color: BRAND }}>{match.matchedName}</span>
                              </div>
                              {orderMat && (
                                <div className="text-[11px] text-gray-500 mt-0.5 ml-6">
                                  замовлення: {orderMat.name}
                                </div>
                              )}
                              {match.isAnalog && match.analogNote && (
                                <div className="text-[11px] text-amber-600 mt-0.5 ml-6 italic">⚠ {match.analogNote}</div>
                              )}
                              {nameChanged && (
                                <label className="flex items-center gap-1.5 mt-1.5 ml-6 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={willRename}
                                    onChange={(e) => setRenameDecisions(prev => ({ ...prev, [match.orderIndex]: e.target.checked }))}
                                    className="w-3.5 h-3.5 rounded"
                                    style={{ accentColor: BRAND }}
                                  />
                                  <span className="text-[11px]" style={{ color: willRename ? BRAND : '#6b7280' }}>
                                    {willRename ? 'Замінити назву в замовленні' : 'Зберегти оригінальну назву'}
                                  </span>
                                </label>
                              )}
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-[13px] font-semibold tabular-nums" style={{ color: BRAND }}>
                                {(match.unitPrice || 0).toLocaleString('uk-UA')} ₴
                              </div>
                              <div className="text-[10px] text-gray-400">
                                {match.qty || 0} {match.unit || 'шт.'}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Unmatched */}
              {quoteResult.unmatched && quoteResult.unmatched.length > 0 && (
                <div className="mb-4">
                  <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Нові позиції з КП ({quoteResult.unmatched.length})</div>
                  <div className="space-y-2">
                    {quoteResult.unmatched.map((u, idx) => (
                      <div key={idx} className="px-3 py-2 rounded-lg flex items-start justify-between gap-2" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium line-clamp-2 break-words" style={{ color: BRAND }}>+ {u.name}</div>
                          <div className="text-[10px] text-gray-500 mt-0.5">Додасться в замовлення</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-[13px] font-semibold tabular-nums" style={{ color: BRAND }}>
                            {(u.unitPrice || 0).toLocaleString('uk-UA')} ₴
                          </div>
                          <div className="text-[10px] text-gray-400">
                            {u.qty || 1} {u.unit || 'шт.'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Not found */}
              {quoteResult.notFound && quoteResult.notFound.length > 0 && (
                <div className="mb-4">
                  <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Не знайдено ціни ({quoteResult.notFound.length})</div>
                  <div className="space-y-1">
                    {quoteResult.notFound.map((idx, i) => {
                      const mat = (editingOrder.materials || [])[idx];
                      if (!mat) return null;
                      return (
                        <div key={i} className="px-3 py-2 rounded-lg flex items-center gap-2" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                          <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#d97706' }} />
                          <span className="text-[12px]" style={{ color: '#78350f' }}>{mat.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Total */}
              {quoteResult.totalSum && (
                <div className="mt-4 pt-3 border-t flex justify-between items-center" style={{ borderColor: '#e5e7eb' }}>
                  <span className="text-sm font-semibold" style={{ color: BRAND }}>Загальна сума КП:</span>
                  <span className="text-lg font-bold tabular-nums" style={{ color: BRAND }}>
                    {quoteResult.totalSum.toLocaleString('uk-UA')} ₴
                  </span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: '#e5e7eb' }}>
              <button onClick={() => setQuoteResult(null)}
                className="flex-1 py-2.5 bg-white rounded-xl font-semibold text-[13px]"
                style={{ color: BRAND, border: '1.5px solid #163248' }}>
                Скасувати
              </button>
              <button onClick={applyQuoteResult}
                className="flex-1 py-2.5 text-white rounded-xl font-semibold text-[13px] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: BRAND }}
                disabled={(quoteResult.matches?.length || 0) === 0 && (quoteResult.unmatched?.length || 0) === 0}>
                Застосувати
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
